import React, { useState, useEffect, useRef } from 'react';
import { Trophy, ChevronLeft, Zap, Crown, Gift, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/feedback';
import { 
  subscribeToBossById, 
  subscribeToTopHunters, 
  subscribeToUserParticipation, 
  queueTapDamage, 
  claimBossReward, 
  getBossCycleInfo,
  BOSS_TEMPLATES 
} from '../services/bossService';

export default function TestBossV2({ currentProfile }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userName = currentProfile?.name || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Athlète';

  // Cible : Le Barromancien (boss spécifique de l'arène /test2 basé sur les répétitions)
  const TARGET_BOSS_ID = 'barromancien';

  // --- ÉTATS TEMPS RÉEL FIRESTORE ---
  const [boss, setBoss] = useState(BOSS_TEMPLATES.barromancien);
  const [topHunters, setTopHunters] = useState([]);
  const [userPart, setUserPart] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);

  // --- ÉTATS LOCAUX OPTIMISTES (RÉACTIVITÉ INSTANTANÉE SANS AUCUNE LATENCE) ---
  const [localBonusDamage, setLocalBonusDamage] = useState(0);
  const [localHpOffset, setLocalHpOffset] = useState(0);

  // Animations & Effets Visuels
  const [isHit, setIsHit] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const [floatingDamages, setFloatingDamages] = useState([]);
  const nextId = useRef(0);

  // 1. Écoute du Barromancien dans Firestore
  useEffect(() => {
    const unsub = subscribeToBossById(TARGET_BOSS_ID, (activeBoss) => {
      if (activeBoss) {
        setBoss(activeBoss);
      }
    });
    return () => unsub();
  }, []);

  // 2. Écoute du Top Chasseurs en répétitions pour Le Barromancien
  useEffect(() => {
    const unsub = subscribeToTopHunters(TARGET_BOSS_ID, (hunters) => {
      setTopHunters(hunters);
    }, 3);
    return () => unsub();
  }, []);

  // 3. Écoute de la Participation en répétitions de l'Utilisateur
  useEffect(() => {
    if (!currentProfile?.id) return;
    const unsub = subscribeToUserParticipation(TARGET_BOSS_ID, currentProfile.id, (part) => {
      setUserPart(part);
    });
    return () => unsub();
  }, [currentProfile?.id]);

  // Réconciliation fluide de l'optimistic UI quand Firestore confirme l'écriture
  const prevServerDamage = useRef(null);
  const prevServerHp = useRef(null);

  useEffect(() => {
    if (userPart?.damage !== undefined) {
      if (prevServerDamage.current !== null) {
        const delta = (userPart.damage || 0) - prevServerDamage.current;
        if (delta > 0) {
          setLocalBonusDamage(prev => Math.max(0, prev - delta));
        }
      }
      prevServerDamage.current = userPart.damage || 0;
    }
  }, [userPart?.damage]);

  useEffect(() => {
    if (boss?.currentHp !== undefined) {
      if (prevServerHp.current !== null) {
        const delta = prevServerHp.current - boss.currentHp;
        if (delta > 0) {
          setLocalHpOffset(prev => Math.max(0, prev - delta));
        }
      }
      prevServerHp.current = boss.currentHp;
    }
  }, [boss?.currentHp]);

  const maxHp = boss.maxHp || 40000;
  const baseHp = boss.currentHp !== undefined ? boss.currentHp : 40000;
  const currentHp = Math.max(0, baseHp - localHpOffset);
  const hpPercent = Math.max(0, Math.min(100, (currentHp / maxHp) * 100));
  const bossCycle = boss?.cycleInfo || getBossCycleInfo();
  const isDead = currentHp <= 0 || boss.status === 'defeated';
  const isRaidExpired = boss.status === 'expired' || (!isDead && bossCycle.isRestPeriod);
  const isCombatOver = isDead || isRaidExpired;
  const isLowHp = currentHp > 0 && hpPercent <= 10 && !isCombatOver;
  const rewardCoins = boss.rewardCoins || 150;
  const damageUnit = boss?.damageUnit || (boss?.ruleType === 'reps' ? 'reps' : (boss?.name === 'Le Barromancien' ? 'reps' : 'kg'));
  const myDamage = (userPart?.damage || 0) + localBonusDamage;
  const userRank = userPart?.rank || (myDamage > 0 ? 1 : null);
  const hasEligibleWorkouts = (userPart?.workoutsCount || 0) >= 1;

  // Nettoyage fluide des dégâts flottants
  useEffect(() => {
    if (floatingDamages.length === 0) return;
    const timer = setTimeout(() => {
      setFloatingDamages(prev => prev.slice(1));
    }, 900);
    return () => clearTimeout(timer);
  }, [floatingDamages]);

  // Déclenchement visuel d'une attaque
  const dealDamage = (amount, isCrit = false, clickX = null, clickY = null, isTap = false) => {
    setIsHit(true);
    setTimeout(() => setIsHit(false), isTap ? 120 : 200);

    if (amount >= 5000) {
      setScreenShake(true);
      setTimeout(() => setScreenShake(false), 350);
    }

    const id = nextId.current++;
    const x = clickX !== null ? clickX : Math.random() * 80 - 40;
    const y = clickY !== null ? clickY : Math.random() * 40 - 20;

    setFloatingDamages(prev => [
      ...prev,
      {
        id,
        amount,
        isCrit,
        isTap,
        x,
        y,
        text: isTap ? `-1 kg 👊` : `-${amount.toLocaleString()} kg${isCrit ? ' 💥' : ''}`
      }
    ]);
  };

  // Clic/Tap direct sur l'illustration du magicien (0ms de latence)
  const handleMageTap = (e) => {
    if (isCombatOver) {
      if (isRaidExpired && !isDead) {
        toast.info("L'arène est en pause. Le prochain boss arrivera lundi prochain !");
      }
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left - rect.width / 2;
    const clickY = e.clientY - rect.top - rect.height / 2;

    dealDamage(1, false, clickX, clickY, true);

    setLocalBonusDamage(prev => prev + 1);
    setLocalHpOffset(prev => prev + 1);

    if (currentProfile?.id) {
      queueTapDamage(currentProfile, (res) => {
        if (res?.isDefeated) {
          toast.success(`🎉 BOSS TERRASSÉ ! Le coup final a achevé Le Barromancien !`, { icon: '👑' });
        }
      }, TARGET_BOSS_ID);
    }
  };

  // Récupération de la récompense de 150 pièces
  const handleClaimReward = async () => {
    if (!currentProfile?.id) {
      toast.error("Sélectionne un profil pour récupérer ton butin.");
      return;
    }
    setIsClaiming(true);
    try {
      const res = await claimBossReward(currentProfile, TARGET_BOSS_ID);
      toast.success(`🎉 Félicitations ! +${res.rewardCoins} Pièces ajoutées à ton compte !`, { icon: '🪙' });
    } catch (err) {
      toast.error(err.message || "Erreur lors de la récupération du butin.");
    } finally {
      setIsClaiming(false);
    }
  };

  // --- CARTES DE DROITE (HARMONISÉES AVEC WORLD BOSS VIEW) ---
  const renderContributionCard = () => (
    <div style={{
      backgroundColor: 'var(--bg-card, #1c1a24)',
      border: '1px solid var(--border-color, #2f2c3d)',
      borderRadius: '16px',
      padding: '1.2rem',
      boxShadow: 'var(--shadow)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: '0.75rem',
      boxSizing: 'border-box'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', fontWeight: 800, color: 'var(--text-secondary, #9a94ab)' }}>
          <Zap size={16} color="var(--primary-color, #8c7fbf)" /> Ta Contribution au Raid
        </div>
        <span style={{
          fontSize: '0.72rem',
          fontWeight: 800,
          color: userRank ? 'var(--secondary-color, #6eb583)' : 'var(--text-secondary, #9a94ab)',
          backgroundColor: userRank ? 'rgba(110, 181, 131, 0.12)' : 'rgba(255, 255, 255, 0.05)',
          padding: '0.15rem 0.5rem',
          borderRadius: '5px'
        }}>
          {userRank ? `Rang #${userRank}` : 'Non classé'}
        </span>
      </div>

      <div>
        <div style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--primary-color-hover, #a89fcf)', lineHeight: 1 }}>
          {myDamage.toLocaleString()} {damageUnit}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #9a94ab)', marginTop: '0.25rem' }}>
          {userPart?.workoutsCount || 0} séance(s) comptabilisée(s) cette semaine
        </div>
      </div>

      {/* BUTIN COLLECTIF */}
      {isDead ? (
        userPart?.hasClaimedReward ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.4rem',
            backgroundColor: 'rgba(110, 181, 131, 0.18)',
            border: '1px solid var(--secondary-color, #6eb583)',
            padding: '0.6rem 0.8rem',
            borderRadius: '10px',
            fontSize: '0.78rem',
            color: 'var(--secondary-color, #6eb583)',
            fontWeight: 800
          }}>
            <Check size={16} /> Butin de {rewardCoins} 🪙 récupéré !
          </div>
        ) : (
          <button
            onClick={handleClaimReward}
            disabled={isClaiming}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(135deg, #df9b3e 0%, #bd7b24 100%)',
              color: '#ffffff',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '12px',
              fontSize: '0.85rem',
              fontWeight: 800,
              cursor: isClaiming ? 'wait' : 'pointer',
              opacity: isClaiming ? 0.7 : 1,
              boxShadow: '0 4px 14px rgba(223, 155, 62, 0.35)',
              transition: 'transform 0.15s ease'
            }}
          >
            <Gift size={18} />
            <span>{isClaiming ? "Récupération en cours..." : `Réclamer mon butin (+${rewardCoins} 🪙) !`}</span>
          </button>
        )
      ) : isRaidExpired ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.45rem',
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid var(--border-color, #2f2c3d)',
          padding: '0.55rem 0.75rem',
          borderRadius: '10px',
          fontSize: '0.76rem',
          color: 'var(--text-secondary, #9a94ab)'
        }}>
          <span>💀 Le boss n'a pas été vaincu à temps et s'est enfui. Rendez-vous au prochain raid !</span>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.45rem',
          backgroundColor: hasEligibleWorkouts ? 'rgba(110, 181, 131, 0.1)' : 'rgba(251, 191, 36, 0.08)',
          border: hasEligibleWorkouts ? '1px solid rgba(110, 181, 131, 0.25)' : '1px solid rgba(251, 191, 36, 0.2)',
          padding: '0.55rem 0.75rem',
          borderRadius: '10px',
          fontSize: '0.76rem',
          color: 'var(--text-primary, #f0eef5)'
        }}>
          <Gift size={16} color={hasEligibleWorkouts ? "var(--secondary-color, #6eb583)" : "#fbbf24"} />
          <span>
            {hasEligibleWorkouts 
              ? <>✓ Tu es <strong>éligible</strong> aux <strong>+{rewardCoins} 🪙</strong> de butin collectif !</>
              : <>Butin collectif : <strong>+{rewardCoins} 🪙</strong> (1 séance requise pour débloquer)</>}
          </span>
        </div>
      )}
    </div>
  );

  const renderTopHuntersCard = () => (
    <div style={{
      backgroundColor: 'var(--bg-card, #1c1a24)',
      border: '1px solid var(--border-color, #2f2c3d)',
      borderRadius: '16px',
      padding: '1.2rem',
      boxShadow: 'var(--shadow)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxSizing: 'border-box'
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Trophy size={18} color="#fbbf24" />
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800 }}>Top meilleurs chasseurs</h3>
          </div>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #9a94ab)' }}>
            {bossCycle.isRestPeriod ? 'Bilan du raid' : 'Tonnage hebdo'}
          </span>
        </div>

        {/* LISTE TOP 3 DES ATHLÈTES RÉELS */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
          {topHunters.length === 0 ? (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #9a94ab)', fontStyle: 'italic', padding: '0.5rem 0' }}>
              Aucun athlète n'a encore enregistré de séance cette semaine.
            </div>
          ) : (
            topHunters.map(hunter => (
              <div 
                key={hunter.rank || hunter.id}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  padding: '0.5rem 0.7rem', 
                  backgroundColor: 'rgba(255,255,255,0.03)', 
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                  <span style={{ fontSize: '1rem' }}>{hunter.medal}</span>
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-primary, #f0eef5)' }}>{hunter.name}</div>
                    <div style={{ fontSize: '0.68rem', color: hunter.isKing ? '#fbbf24' : 'var(--primary-color-hover, #a89fcf)', fontWeight: 600 }}>
                      {hunter.title}
                    </div>
                  </div>
                </div>
                <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-secondary, #9a94ab)' }}>
                  {(hunter.damage || 0).toLocaleString()} {damageUnit}
                </span>
              </div>
            ))
          )}

          {/* BANNIÈRE DE L'UTILISATEUR CONNECTÉ */}
          <div style={{
            marginTop: '0.45rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.65rem 0.75rem',
            backgroundColor: 'rgba(140, 127, 191, 0.14)',
            border: '1.5px solid var(--primary-color, #8c7fbf)',
            borderRadius: '12px',
            boxShadow: '0 0 16px rgba(140, 127, 191, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{
                fontSize: '0.72rem',
                fontWeight: 900,
                backgroundColor: 'var(--primary-color, #8c7fbf)',
                color: '#fff',
                padding: '0.18rem 0.45rem',
                borderRadius: '6px'
              }}>
                {userRank ? `#${userRank}` : '-'}
              </div>
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-primary, #f0eef5)' }}>
                  Toi ({userName})
                </div>
                <div style={{
                  fontSize: '0.68rem',
                  color: hasEligibleWorkouts ? 'var(--secondary-color, #6eb583)' : 'var(--text-secondary, #9a94ab)',
                  fontWeight: 600
                }}>
                  {isDead ? (userPart?.hasClaimedReward ? "✓ Butin récupéré" : `🎁 Butin prêt (+${rewardCoins} 🪙)`) : (hasEligibleWorkouts ? `✓ Éligible au Butin (+${rewardCoins} 🪙)` : `0 séance (non éligible)`)}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 900, color: 'var(--primary-color-hover, #a89fcf)' }}>
                {myDamage.toLocaleString()} {damageUnit}
              </span>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary, #9a94ab)' }}>
                {userRank ? `Rang #${userRank}` : 'Non classé'}
              </div>
            </div>
          </div>

        </div>
      </div>
      
      <div style={{ marginTop: '0.8rem', fontSize: '0.7rem', color: 'var(--text-secondary, #9a94ab)', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.5rem' }}>
        Le Top 3 et le coup final remportent les titres de prestige à la défaite du boss.
      </div>
    </div>
  );

  const renderPrestigeTitlesCard = () => (
    <div style={{
      backgroundColor: 'var(--bg-card, #1c1a24)',
      border: '1px solid var(--border-color, #2f2c3d)',
      borderRadius: '16px',
      padding: '1.2rem',
      boxShadow: 'var(--shadow)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: '0.5rem',
      boxSizing: 'border-box'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', fontWeight: 800, color: 'var(--text-secondary, #9a94ab)' }}>
        <Crown size={16} color="#fbbf24" /> Titres en jeu (7 jours)
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
          <span>👑 <strong>Top 1</strong></span>
          <span style={{ color: '#fbbf24', fontWeight: 700 }}>Fléau des Titans</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
          <span>⚔️ <strong>Top 2-3</strong></span>
          <span style={{ color: 'var(--primary-color-hover, #a89fcf)', fontWeight: 700 }}>Champion du Raid</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
          <span>🎯 <strong>Dernier Coup</strong></span>
          <span style={{ color: 'var(--secondary-color, #6eb583)', fontWeight: 700 }}>Coup de Grâce</span>
        </div>
      </div>

      <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary, #9a94ab)', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.4rem' }}>
        Attribués directement sur le profil dès la mort du boss (actifs jusqu'au prochain boss).
      </div>
    </div>
  );

  return (
    <div className={`boss-arena-wrapper ${screenShake ? 'shake-screen' : ''}`} style={{
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      color: 'var(--text-primary, #f0eef5)',
      paddingBottom: '2.5rem',
      position: 'relative'
    }}>
      <style>{`
        @keyframes floatMage {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(0.6deg); }
        }
        @keyframes orbPulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 10px #a855f7) drop-shadow(0 0 20px #6d28d9); }
          50% { transform: scale(1.1); filter: drop-shadow(0 0 16px #a855f7) drop-shadow(0 0 30px #ec4899); }
        }
        @keyframes rotateRunes {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes rotateRunesRev {
          0% { transform: rotate(360deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes eyeGlint {
          0%, 100% { filter: drop-shadow(0 0 4px var(--primary-color, #8c7fbf)) drop-shadow(0 0 8px var(--secondary-color, #6eb583)); opacity: 0.95; }
          50% { filter: drop-shadow(0 0 8px #a855f7) drop-shadow(0 0 15px #10b981); opacity: 1; }
        }
        @keyframes spellCast {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-4px) scale(1.04); }
        }
        @keyframes damagePop {
          0% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.6); }
          20% { opacity: 1; transform: translate(var(--dx), calc(var(--dy) - 20px)) scale(1.15); }
          100% { opacity: 0; transform: translate(var(--dx), calc(var(--dy) - 55px)) scale(0.9); }
        }
        .shake-screen {
          animation: shake 0.35s cubic-bezier(.36,.07,.19,.97) both;
        }
        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
        .mage-container {
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: transform 0.08s ease;
        }
        .mage-container:active {
          transform: scale(0.96) !important;
        }
        .mage-container.hit svg {
          filter: brightness(2) contrast(1.5) drop-shadow(0 0 25px #ffffff) !important;
          transition: filter 0.05s ease;
        }

        /* --- LAYOUT MOBILE PAR DÉFAUT (INCHANGÉ, PARFAIT SUR TÉLÉPHONE) --- */
        .boss-main-layout {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          width: 100%;
        }
        .boss-arena-box {
          width: 100%;
          box-sizing: border-box;
        }
        .boss-arena-center {
          width: 100%;
          margin: 0.2rem auto 0.5rem auto;
        }
        .dashboard-cards-col {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          width: 100%;
        }

        /* --- LAYOUT PC / GRAND ÉCRAN (2 COLONNES HARMONIEUSES, MÊME HAUTEUR EXACTE) --- */
        @media (min-width: 901px) {
          .boss-main-layout {
            display: grid !important;
            grid-template-columns: 1.15fr 0.85fr !important;
            gap: 1.5rem !important;
            align-items: stretch !important;
          }
          .boss-arena-box {
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: space-between !important;
            padding: 1.8rem 1.4rem !important;
            box-sizing: border-box !important;
          }
          .boss-arena-top {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            width: 100% !important;
          }
          .boss-arena-center {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            min-height: 280px !important;
            margin: 1rem 0 !important;
            width: 100% !important;
          }
          .boss-arena-center .mage-container {
            max-width: 400px !important;
          }
          .boss-arena-center .mage-container svg {
            max-width: 380px !important;
          }
          .boss-arena-bottom {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            width: 100% !important;
          }
          .dashboard-cards-col {
            display: flex !important;
            flex-direction: column !important;
            gap: 1rem !important;
            height: 100% !important;
            justify-content: space-between !important;
          }
        }
      `}</style>

      {/* Halo lumineux d'ambiance (exactement comme sur /social?tab=boss) */}
      <div style={{
        position: 'absolute',
        top: '0%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(140, 127, 191, 0.14) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <div style={{ width: '100%', position: 'relative', zIndex: 1, boxSizing: 'border-box' }}>

        {/* NAVIGATION HAUTE AVEC SWITCHER DE BOSS */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          marginBottom: '1rem', 
          width: '100%',
          flexWrap: 'wrap',
          gap: '0.6rem'
        }}>
          <button 
            onClick={() => navigate('/social?tab=boss')}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              backgroundColor: 'var(--bg-card, #1c1a24)', border: '1px solid var(--border-color, #2f2c3d)',
              color: 'var(--text-secondary, #9a94ab)', padding: '0.45rem 0.85rem', borderRadius: '10px',
              cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
              boxShadow: 'var(--shadow)'
            }}
          >
            <ChevronLeft size={16} /> Retour au Social
          </button>

          {/* Switcher d'arène Boss */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: 'var(--bg-card, #1c1a24)',
            border: '1px solid var(--border-color, #2f2c3d)',
            borderRadius: '12px',
            padding: '3px',
            gap: '4px'
          }}>
            <button
              style={{
                border: 'none',
                background: 'rgba(140, 127, 191, 0.25)',
                color: 'var(--primary-color-hover, #a89fcf)',
                padding: '0.35rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.78rem',
                fontWeight: 800,
                cursor: 'default'
              }}
            >
              🧙 Barromancien (Reps)
            </button>
            <button
              onClick={() => navigate('/test3')}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--text-secondary, #9a94ab)',
                padding: '0.35rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              🗿 MusculaThor (Temps)
            </button>
          </div>
        </div>

        {/* CONTENU PRINCIPAL : LAYOUT 2 COLONNES HARMONIEUX */}
        <div className="boss-main-layout">

          {/* COLONNE GAUCHE : ARÈNE DU BOSS */}
          <div className="boss-arena-box" style={{
            backgroundColor: 'var(--bg-card, #1c1a24)',
            border: isLowHp 
              ? '1.5px solid var(--danger-color, #ef4444)' 
              : '1px solid var(--border-color, #2f2c3d)',
            boxShadow: isLowHp 
              ? '0 0 20px rgba(239, 68, 68, 0.22), var(--shadow)' 
              : 'var(--shadow)',
            borderRadius: '20px',
            padding: '1.3rem 1rem',
            textAlign: 'center',
            position: 'relative',
            boxSizing: 'border-box',
            transition: 'border 0.3s ease, box-shadow 0.3s ease'
          }}>

            <div className="boss-arena-top" style={{ width: '100%' }}>
              {/* BADGES & STATUT DU BOSS */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  padding: '0.2rem 0.55rem',
                  backgroundColor: isDead 
                    ? 'rgba(110, 181, 131, 0.18)' 
                    : 'rgba(140, 127, 191, 0.18)',
                  color: isDead 
                    ? 'var(--secondary-color, #6eb583)' 
                    : 'var(--primary-color-hover, #a89fcf)',
                  borderRadius: '20px',
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px'
                }}>
                  {isDead ? '🏆 Boss Vaincu' : '⚔️ Évènement Spécial'}
                </span>

                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  padding: '0.2rem 0.55rem',
                  backgroundColor: 'rgba(110, 181, 131, 0.12)',
                  color: 'var(--secondary-color, #6eb583)',
                  borderRadius: '20px',
                  fontSize: '0.68rem',
                  fontWeight: 700
                }}>
                  ⚖️ {boss?.ruleLabel || '1 répétition = 1 dégât'}
                </span>
              </div>

              {/* NOM DU BOSS */}
              <h1 style={{
                margin: '0.2rem 0 0.15rem 0',
                fontSize: '1.9rem',
                fontWeight: 900,
                letterSpacing: '-0.5px',
                color: 'var(--text-primary, #f0eef5)'
              }}>
                Le Barromancien
              </h1>
              <p style={{
                margin: '0 0 0.9rem 0',
                fontSize: '0.82rem',
                color: 'var(--text-secondary, #9a94ab)',
                fontStyle: 'italic'
              }}>
                L'Archimage de la Charge Pure
              </p>

              {/* BARRE DE VIE RÉACTOR (LIVE SYNC & INSTANT FEEDBACK) */}
              <div style={{ maxWidth: '440px', margin: '0 auto 0.75rem auto', width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.35rem', fontSize: '0.78rem' }}>
                  <span style={{ color: isLowHp ? 'var(--danger-color, #ef4444)' : 'var(--text-secondary, #9a94ab)', fontWeight: 600, transition: 'color 0.3s ease' }}>Points de Vie</span>
                  <span style={{ fontWeight: 900, color: isDead ? '#6b7280' : (isLowHp ? 'var(--danger-color, #ef4444)' : 'var(--text-primary, #f0eef5)'), transition: 'color 0.3s ease' }}>
                    {currentHp.toLocaleString()} / {maxHp.toLocaleString()} PV ({hpPercent.toFixed(1)}%)
                  </span>
                </div>

                <div style={{
                  width: '100%',
                  height: '14px',
                  backgroundColor: 'rgba(0, 0, 0, 0.35)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color, #2f2c3d)',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.5)'
                }}>
                  <div style={{
                    width: `${hpPercent}%`,
                    height: '100%',
                    background: isDead
                      ? 'var(--text-secondary, #6b7280)'
                      : isLowHp
                        ? 'linear-gradient(90deg, #ec4899 0%, #f43f5e 100%)'
                        : 'linear-gradient(90deg, #6d28d9 0%, var(--primary-color, #8c7fbf) 50%, var(--secondary-color, #6eb583) 100%)',
                    borderRadius: '8px',
                    transition: 'width 0.25s cubic-bezier(0.2, 0.8, 0.2, 1)'
                  }} />
                </div>
              </div>
            </div>

            {/* CENTRE DU BLOC : ILLUSTRATION DU MAGICIEN */}
            <div className="boss-arena-center">
              <div
                className={`mage-container ${isHit ? 'hit' : ''}`}
                onClick={handleMageTap}
                title={isDead ? "Le boss a été vaincu !" : "Tapote avec ton doigt ou ta souris pour frapper !"}
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '320px',
                  margin: '0.2rem auto 0.5rem auto',
                  filter: isDead ? 'grayscale(1) opacity(0.35)' : 'none',
                  transition: 'filter 0.3s'
                }}
              >
                {/* SVG ILLUSTRATION DU MAGICIEN (STYLISTE, VIBRANT, DÉTAILLÉ) */}
                <svg
                  viewBox="0 0 340 340"
                  style={{
                    width: '100%',
                    height: 'auto',
                    overflow: 'visible',
                    animation: isDead ? 'none' : 'floatMage 3.8s ease-in-out infinite'
                  }}
                >
                  <defs>
                    {/* Filtre de lueur magique intense */}
                    <filter id="mageGlow" x="-20%" y="-20%" width="140%" height="140%">
                      <feGaussianBlur stdDeviation="5" result="blur" />
                      <feComposite in="SourceGraphic" in2="blur" operator="over" />
                    </filter>
                    <filter id="manaPulse" x="-30%" y="-30%" width="160%" height="160%">
                      <feGaussianBlur stdDeviation="8" result="blur" />
                      <feComposite in="SourceGraphic" in2="blur" operator="over" />
                    </filter>

                    {/* Dégradés Arcaniques */}
                    <linearGradient id="robeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1e113a" />
                      <stop offset="50%" stopColor="#3b1d6e" />
                      <stop offset="100%" stopColor="#150a28" />
                    </linearGradient>

                    <linearGradient id="robeFold" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#2e1457" />
                      <stop offset="50%" stopColor="#4f268f" />
                      <stop offset="100%" stopColor="#1c0b36" />
                    </linearGradient>

                    <linearGradient id="capeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#0f172a" />
                      <stop offset="60%" stopColor="#1e1b4b" />
                      <stop offset="100%" stopColor="#090d16" />
                    </linearGradient>

                    <radialGradient id="manaOrbGrad" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#ffffff" />
                      <stop offset="25%" stopColor="#c084fc" />
                      <stop offset="65%" stopColor="#7c3aed" />
                      <stop offset="100%" stopColor="#4338ca" />
                    </radialGradient>

                    <linearGradient id="staffWood" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#451a03" />
                      <stop offset="50%" stopColor="#78350f" />
                      <stop offset="100%" stopColor="#270e02" />
                    </linearGradient>

                    <linearGradient id="goldFiligree" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#fbbf24" />
                      <stop offset="50%" stopColor="#f59e0b" />
                      <stop offset="100%" stopColor="#b45309" />
                    </linearGradient>

                    <linearGradient id="weightPlateGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1e293b" />
                      <stop offset="50%" stopColor="#334155" />
                      <stop offset="100%" stopColor="#0f172a" />
                    </linearGradient>
                  </defs>

                  {/* 1. RUNES ET CERCLE ARCANISTE EN ARRIÈRE-PLAN */}
                  <g style={{ transformOrigin: '170px 170px', animation: isDead ? 'none' : 'rotateRunes 35s linear infinite' }}>
                    <circle cx="170" cy="170" r="145" fill="none" stroke="rgba(140, 127, 191, 0.22)" strokeWidth="1.5" strokeDasharray="8 6" />
                    <circle cx="170" cy="170" r="132" fill="none" stroke="rgba(168, 85, 247, 0.25)" strokeWidth="1" />
                    <circle cx="170" cy="170" r="118" fill="none" stroke="rgba(110, 181, 131, 0.2)" strokeWidth="1" strokeDasharray="4 8" />
                    
                    {/* Symboles mystiques d'angle */}
                    <text x="165" y="18" fill="var(--primary-color-hover, #a89fcf)" fontSize="13" opacity="0.6">⚡</text>
                    <text x="165" y="332" fill="var(--primary-color-hover, #a89fcf)" fontSize="13" opacity="0.6">⚡</text>
                    <text x="18" y="175" fill="var(--secondary-color, #6eb583)" fontSize="13" opacity="0.6">✦</text>
                    <text x="312" y="175" fill="var(--secondary-color, #6eb583)" fontSize="13" opacity="0.6">✦</text>
                  </g>

                  {/* 2. DISQUES DE FONTE EN SUSTENTATION MAGIQUE EN ORBITE */}
                  {/* Disque gauche 10 kg */}
                  <g style={{ transformOrigin: '55px 145px', animation: isDead ? 'none' : 'rotateRunesRev 18s linear infinite' }}>
                    <circle cx="55" cy="145" r="24" fill="url(#weightPlateGrad)" stroke="var(--primary-color, #8c7fbf)" strokeWidth="2" filter="drop-shadow(0 0 8px rgba(140, 127, 191, 0.4))" />
                    <circle cx="55" cy="145" r="15" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
                    <circle cx="55" cy="145" r="7" fill="#0f172a" stroke="var(--primary-color, #8c7fbf)" strokeWidth="1.5" />
                    <text x="47" y="149" fill="#94a3b8" fontSize="9" fontWeight="900">10</text>
                  </g>
                  {/* Arcs d'énergie reliant le disque gauche */}
                  <path d="M 78 145 Q 110 160 135 170" fill="none" stroke="rgba(140, 127, 191, 0.35)" strokeWidth="1.5" strokeDasharray="4 4" />

                  {/* Disque droit 20 kg */}
                  <g style={{ transformOrigin: '285px 195px', animation: isDead ? 'none' : 'rotateRunes 22s linear infinite' }}>
                    <circle cx="285" cy="195" r="28" fill="url(#weightPlateGrad)" stroke="#a855f7" strokeWidth="2" filter="drop-shadow(0 0 10px rgba(168, 85, 247, 0.45))" />
                    <circle cx="285" cy="195" r="18" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
                    <circle cx="285" cy="195" r="8" fill="#0f172a" stroke="#a855f7" strokeWidth="1.5" />
                    <text x="276" y="200" fill="#c084fc" fontSize="11" fontWeight="900">20</text>
                  </g>
                  <path d="M 257 195 Q 230 185 205 180" fill="none" stroke="rgba(168, 85, 247, 0.35)" strokeWidth="1.5" strokeDasharray="4 4" />

                  {/* 3. CAPE MAJESTUEUSE ARRIÈRE */}
                  <path d="M 125 100 C 95 150 75 250 90 295 C 120 285 145 280 170 282 C 195 280 220 285 250 295 C 265 250 245 150 215 100 Z" fill="url(#capeGrad)" stroke="#020617" strokeWidth="2" />
                  {/* Doublure dorée de la cape */}
                  <path d="M 90 295 C 120 285 145 280 170 282 C 195 280 220 285 250 295" fill="none" stroke="url(#goldFiligree)" strokeWidth="3" />

                  {/* 4. CORPS & ROBE DE L'ARCHIMAGE */}
                  {/* Base de la robe */}
                  <path d="M 135 110 L 115 285 C 145 292 195 292 225 285 L 205 110 Z" fill="url(#robeGrad)" stroke="#1e113a" strokeWidth="2" />
                  
                  {/* Plis et pans intérieurs de la robe */}
                  <path d="M 148 120 L 138 287 C 158 291 182 291 202 287 L 192 120 Z" fill="url(#robeFold)" opacity="0.8" />
                  
                  {/* Bande centrale brodée de runes dorées */}
                  <rect x="162" y="130" width="16" height="155" fill="#180a30" stroke="url(#goldFiligree)" strokeWidth="1.5" rx="3" />
                  {/* Glyphes runiques verticaux */}
                  <text x="165" y="152" fill="#fbbf24" fontSize="11" fontWeight="bold">⛯</text>
                  <text x="166" y="180" fill="var(--primary-color-hover, #a89fcf)" fontSize="10">⚡</text>
                  <text x="165" y="208" fill="#fbbf24" fontSize="11" fontWeight="bold">✦</text>
                  <text x="166" y="236" fill="var(--primary-color-hover, #a89fcf)" fontSize="10">⚡</text>
                  <text x="165" y="264" fill="#fbbf24" fontSize="11" fontWeight="bold">⛯</text>

                  {/* Ceinture en cuir lourd et boucle d'haltère magique */}
                  <rect x="133" y="165" width="74" height="14" rx="4" fill="#2d1305" stroke="#78350f" strokeWidth="2" />
                  {/* Boucle haltère */}
                  <rect x="157" y="160" width="26" height="24" rx="6" fill="#170928" stroke="url(#goldFiligree)" strokeWidth="2" />
                  <circle cx="170" cy="172" r="5" fill="var(--secondary-color, #6eb583)" filter="drop-shadow(0 0 5px #6eb583)" />

                  {/* 5. ÉPAULIÈRES MASSIFS EN DEMI-DISQUES DE FONTE */}
                  {/* Épaulière gauche */}
                  <path d="M 115 105 C 100 108 90 120 95 138 C 112 135 125 125 128 112 Z" fill="url(#weightPlateGrad)" stroke="var(--primary-color, #8c7fbf)" strokeWidth="2" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.5))" />
                  <path d="M 98 122 C 108 120 118 115 122 108" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
                  
                  {/* Épaulière droite */}
                  <path d="M 225 105 C 240 108 250 120 245 138 C 228 135 215 125 212 112 Z" fill="url(#weightPlateGrad)" stroke="var(--primary-color, #8c7fbf)" strokeWidth="2" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.5))" />
                  <path d="M 242 122 C 232 120 222 115 218 108" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />

                  {/* 6. BARBE BLANCHE DE SAGE ET VISAGE MYSTIQUE */}
                  {/* Longue barbe flottante */}
                  <path d="M 152 110 C 145 155 155 195 170 215 C 185 195 195 155 188 110 C 178 125 162 125 152 110 Z" fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="1.5" filter="drop-shadow(0 6px 8px rgba(0,0,0,0.4))" />
                  <path d="M 160 125 C 158 160 165 185 170 205" fill="none" stroke="#94a3b8" strokeWidth="1.2" opacity="0.6" />
                  <path d="M 180 125 C 182 160 175 185 170 205" fill="none" stroke="#94a3b8" strokeWidth="1.2" opacity="0.6" />

                  {/* Ombre profonde sous la capuche */}
                  <ellipse cx="170" cy="98" rx="28" ry="24" fill="#090514" />

                  {/* YEUX VIOLET ET VERT ÉTINCELANTS DE PUISSANCE */}
                  <g style={{ animation: isDead ? 'none' : 'eyeGlint 2s infinite' }}>
                    <ellipse cx="158" cy="95" rx="5" ry="3" fill="var(--primary-color-hover, #a89fcf)" filter="url(#mageGlow)" />
                    <ellipse cx="182" cy="95" rx="5" ry="3" fill="var(--primary-color-hover, #a89fcf)" filter="url(#mageGlow)" />
                    <circle cx="158" cy="95" r="2" fill="#ffffff" />
                    <circle cx="182" cy="95" r="2" fill="#ffffff" />
                  </g>

                  {/* 7. CAPUCHE ET CHAPEAU D'ARCHIMAGE MAJESTUEUX */}
                  {/* Capuche drapée */}
                  <path d="M 132 108 C 130 80 145 65 170 65 C 195 65 210 80 208 108 C 215 118 200 128 170 128 C 140 128 125 118 132 108 Z" fill="url(#robeGrad)" stroke="#1e113a" strokeWidth="2.5" />
                  
                  {/* Pointe du chapeau de sorcier inclinée avec bordures dorées */}
                  <path d="M 142 75 C 148 45 160 25 185 15 C 180 32 188 52 198 72 Z" fill="#241147" stroke="url(#goldFiligree)" strokeWidth="2" />
                  
                  {/* Gemme étoilée au bout de la pointe */}
                  <circle cx="185" cy="15" r="4.5" fill="#fbbf24" filter="drop-shadow(0 0 6px #fbbf24)" />

                  {/* Broche magique sur le col */}
                  <polygon points="170,118 176,126 170,134 164,126" fill="#fbbf24" stroke="#d97706" strokeWidth="1.5" />

                  {/* 8. BRAS GAUCHE (CANALISATION DU SORT - ÉNERGIE PURE) */}
                  <path d="M 125 125 L 85 165 L 75 180 L 85 190 L 102 182 L 132 145 Z" fill="#2d1354" stroke="#1c0b36" strokeWidth="2" />
                  {/* Main / gantelet runique gauche */}
                  <circle cx="78" cy="183" r="10" fill="#1e1b4b" stroke="url(#goldFiligree)" strokeWidth="1.5" />
                  {/* Énergie magique crépitante dans la main gauche */}
                  <g style={{ animation: isDead ? 'none' : 'spellCast 1.6s ease-in-out infinite' }}>
                    <circle cx="75" cy="183" r="16" fill="rgba(140, 127, 191, 0.25)" filter="url(#manaPulse)" />
                    <circle cx="75" cy="183" r="8" fill="#e0f2fe" filter="drop-shadow(0 0 10px #a855f7)" />
                    <path d="M 70 170 Q 60 185 72 198" fill="none" stroke="var(--primary-color-hover, #a89fcf)" strokeWidth="2" />
                    <path d="M 82 172 Q 92 182 80 196" fill="none" stroke="#a855f7" strokeWidth="1.5" />
                  </g>

                  {/* 9. BRAS DROIT ET LE BÂTON DE PESANTEUR (AVEC DISQUE DE 25 KG ET ORBE) */}
                  <path d="M 215 125 L 245 160 L 255 178 L 245 188 L 230 178 L 208 145 Z" fill="#2d1354" stroke="#1c0b36" strokeWidth="2" />
                  {/* Main droite tenant le bâton */}
                  <circle cx="250" cy="180" r="10" fill="#1e1b4b" stroke="url(#goldFiligree)" strokeWidth="1.5" />

                  {/* HAMPE DU BÂTON ANCIEN */}
                  <line x1="250" y1="55" x2="250" y2="305" stroke="url(#staffWood)" strokeWidth="8" strokeLinecap="round" />
                  <line x1="248" y1="55" x2="248" y2="305" stroke="url(#goldFiligree)" strokeWidth="2" />

                  {/* Sommet du Bâton : Griffes tenant le disque de 25 kg */}
                  <path d="M 240 70 C 235 50 240 40 250 35 C 260 40 265 50 260 70 Z" fill="url(#goldFiligree)" stroke="#92400e" strokeWidth="1.5" />

                  {/* LE DISQUE DE 25 KG SUR LE BÂTON */}
                  <g style={{ transformOrigin: '250px 45px', animation: isDead ? 'none' : 'rotateRunes 15s linear infinite' }}>
                    <circle cx="250" cy="45" r="32" fill="url(#weightPlateGrad)" stroke="var(--primary-color, #8c7fbf)" strokeWidth="2.5" filter="drop-shadow(0 0 12px rgba(140, 127, 191, 0.5))" />
                    <circle cx="250" cy="45" r="22" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.2" />
                    <circle cx="250" cy="45" r="11" fill="#0f172a" stroke="var(--primary-color, #8c7fbf)" strokeWidth="2" />
                    <text x="242" y="38" fill="var(--primary-color-hover, #a89fcf)" fontSize="10" fontWeight="900">25</text>
                    <text x="238" y="58" fill="#94a3b8" fontSize="8" fontWeight="bold">OLYMPIC</text>
                  </g>

                  {/* L'ORBE DE MANA INCANDESCENT AU COEUR DU DISQUE */}
                  <g style={{ transformOrigin: '250px 45px', animation: isDead ? 'none' : 'orbPulse 2.4s ease-in-out infinite' }}>
                    <circle cx="250" cy="45" r="14" fill="url(#manaOrbGrad)" filter="url(#manaPulse)" />
                    <circle cx="247" cy="42" r="4" fill="#ffffff" opacity="0.9" />
                  </g>

                  {/* Anneaux d'énergie arcanique tournoyant autour de l'orbe */}
                  <ellipse cx="250" cy="45" rx="20" ry="8" fill="none" stroke="var(--primary-color-hover, #a89fcf)" strokeWidth="1.8" strokeDasharray="5 3" transform="rotate(25 250 45)" />
                  <ellipse cx="250" cy="45" rx="20" ry="8" fill="none" stroke="#c084fc" strokeWidth="1.5" strokeDasharray="4 4" transform="rotate(-35 250 45)" />

                </svg>

                {/* DÉGÂTS FLOTTANTS MAGIQUES */}
                {floatingDamages.map(item => (
                  <div
                    key={item.id}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      '--dx': `${item.x}px`,
                      '--dy': `${item.y}px`,
                      animation: 'damagePop 0.9s cubic-bezier(0.18, 0.89, 0.32, 1.28) forwards',
                      pointerEvents: 'none',
                      fontSize: item.isTap ? '0.95rem' : (item.isCrit ? '1.45rem' : '1.15rem'),
                      fontWeight: 900,
                      color: item.isTap ? '#fbbf24' : (item.isCrit ? '#ef4444' : 'var(--primary-color-hover, #a89fcf)'),
                      textShadow: '0 2px 10px rgba(0,0,0,0.95), 0 0 14px rgba(140, 127, 191, 0.8)',
                      whiteSpace: 'nowrap',
                      zIndex: 10
                    }}
                  >
                    {item.text}
                  </div>
                ))}
              </div>
            </div>

            {/* BAS DU BLOC : TAUNT & INDICATION */}
            <div className="boss-arena-bottom" style={{ width: '100%' }}>
              {/* TAUNT DU BOSS */}
              <div style={{
                fontSize: '0.82rem',
                color: isDead ? '#6b7280' : 'var(--text-secondary, #9a94ab)',
                fontWeight: 700,
                margin: '0.4rem 0 0.8rem 0'
              }}>
                « {isDead 
                  ? (boss.killer ? `Vaincu par ${boss.killer.name} et toute la commu !` : 'Argh... La commu a vaincu !')
                  : "Ton shaker phosphorescent ne va pas soulever la barre à ta place."} »
              </div>

              {/* INDICATION DE COMBAT NATURELLE */}
              {!isDead && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  padding: '0.5rem 0.85rem',
                  borderRadius: '12px',
                  fontSize: '0.74rem',
                  color: 'var(--text-secondary, #9a94ab)',
                  lineHeight: 1.4,
                  maxWidth: '92%'
                }}>
                  <span>💡 Tapote avec ton doigt ou ta souris pour frapper, ou termine tes vraies séances en salle pour lui infliger de lourds dégâts réels !</span>
                </div>
              )}
            </div>

          </div>

          {/* COLONNE DROITE : STATS & CLASSEMENT */}
          <div className="dashboard-cards-col">
            {renderContributionCard()}
            {renderTopHuntersCard()}
            {renderPrestigeTitlesCard()}
          </div>

        </div>

      </div>
    </div>
  );
}
